import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
  closestCenter,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task, TaskStatus } from '../types';
import { TaskCard } from './TaskCard';

const COLUMNS: Array<{
  id: TaskStatus;
  label: string;
  dotColor: string;
  headerBorder: string;
  headerBg: string;
  overBg: string;
}> = [
  { id: 'p1_today',    label: 'P1 — Must Be Done Today', dotColor: 'bg-orange-500', headerBorder: 'border-orange-700', headerBg: 'bg-orange-950/40', overBg: 'bg-orange-900/20' },
  { id: 'not_started', label: 'Not Started',              dotColor: 'bg-gray-500',   headerBorder: 'border-gray-700',   headerBg: '',                 overBg: 'bg-gray-800/40'   },
  { id: 'in_progress', label: 'In Progress',              dotColor: 'bg-blue-500',   headerBorder: 'border-blue-800',   headerBg: '',                 overBg: 'bg-blue-900/20'   },
  { id: 'blocked',     label: 'Blocked',                  dotColor: 'bg-red-500',    headerBorder: 'border-red-900',    headerBg: '',                 overBg: 'bg-red-900/20'    },
  { id: 'completed',   label: 'Completed',                dotColor: 'bg-green-500',  headerBorder: 'border-green-900',  headerBg: '',                 overBg: 'bg-green-900/20'  },
];

const VALID_STATUSES = new Set<string>(COLUMNS.map((c) => c.id));

// pointerWithin first: detects the column container the cursor is inside (critical
// for empty columns that have no sortable children to trigger closestCenter).
// When the pointer is over a card AND its column both register, prefer the card so
// within-column reordering keeps working. Fall back to closestCenter for edge cases.
const collisionDetection: CollisionDetection = (args) => {
  const ptr = pointerWithin(args);
  if (ptr.length > 0) {
    const cardHits = ptr.filter((c) => !VALID_STATUSES.has(c.id as string));
    return cardHits.length > 0 ? cardHits : ptr;
  }
  return closestCenter(args);
};

export interface CardCallbacks {
  onComplete: (id: string, completed: boolean) => void;
  onDelete: (id: string) => void;
  onDueDateChange: (id: string, date: string | undefined) => void;
  onProjectChange: (id: string, project: string | undefined) => void;
  onSelect: (task: Task) => void;
}

// ─── Sortable card wrapper ────────────────────────────────────────────────────

function SortableCard({ task, callbacks }: { task: Task; callbacks: CardCallbacks }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { status: task.status },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
      }}
      {...attributes}
      {...listeners}
      className={`touch-none ${isDragging ? 'opacity-30 cursor-grabbing' : 'cursor-grab'}`}
      onClick={() => callbacks.onSelect(task)}
    >
      <TaskCard
        task={task}
        onComplete={callbacks.onComplete}
        onDelete={callbacks.onDelete}
        onDueDateChange={callbacks.onDueDateChange}
        onProjectChange={callbacks.onProjectChange}
      />
    </div>
  );
}

// ─── Column ───────────────────────────────────────────────────────────────────

interface ColumnProps {
  id: TaskStatus;
  label: string;
  dotColor: string;
  headerBorder: string;
  headerBg: string;
  overBg: string;
  tasks: Task[];
  callbacks: CardCallbacks;
}

function KanbanColumn({ id, label, dotColor, headerBorder, headerBg, overBg, tasks, callbacks }: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { type: 'column' } });
  const taskIds = tasks.map((t) => t.id);

  return (
    <div className="flex flex-col w-72 shrink-0 h-full">
      <div className={`flex items-center gap-2 px-1 py-2 mb-2 border-b shrink-0 rounded-t ${headerBorder} ${headerBg}`}>
        <span className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
        <span className="ml-auto text-xs text-gray-600">{tasks.length}</span>
      </div>

      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`flex-1 overflow-y-auto flex flex-col gap-2 rounded-lg p-1 transition-colors min-h-24 ${
            isOver ? `${overBg} ring-1 ring-inset ring-gray-600` : ''
          }`}
        >
          {tasks.length === 0 ? (
            <div
              className={`flex items-center justify-center h-16 rounded border-2 border-dashed text-xs transition-colors ${
                isOver
                  ? 'border-gray-500 text-gray-400'
                  : 'border-gray-800 text-gray-700'
              }`}
            >
              Drop tasks here
            </div>
          ) : (
            tasks.map((task) => (
              <SortableCard key={task.id} task={task} callbacks={callbacks} />
            ))
          )}
          <div className="shrink-0 h-8" />
        </div>
      </SortableContext>
    </div>
  );
}

// ─── Board ────────────────────────────────────────────────────────────────────

interface KanbanBoardProps {
  tasks: Task[];
  onReorder: (updates: Array<{ id: string; order: number; status?: TaskStatus }>) => void;
  callbacks: CardCallbacks;
}

export function KanbanBoard({ tasks, onReorder, callbacks }: KanbanBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeTask = tasks.find((t) => t.id === activeId);
    if (!activeTask) return;

    const overTask = tasks.find((t) => t.id === overId);
    const isOverColumn = VALID_STATUSES.has(overId) && !overTask;

    const targetStatus: TaskStatus = overTask
      ? overTask.status
      : isOverColumn
      ? (overId as TaskStatus)
      : activeTask.status;

    const sameColumn = targetStatus === activeTask.status;

    // Dropped on same column container with no target task — no change
    if (sameColumn && !overTask) return;

    const updates: Array<{ id: string; order: number; status?: TaskStatus }> = [];

    if (sameColumn) {
      const colTasks = tasks
        .filter((t) => t.status === targetStatus)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const fromIdx = colTasks.findIndex((t) => t.id === activeId);
      const toIdx = colTasks.findIndex((t) => t.id === overId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

      arrayMove(colTasks, fromIdx, toIdx).forEach((t, i) =>
        updates.push({ id: t.id, order: i }),
      );
    } else {
      // Reassign source column orders
      tasks
        .filter((t) => t.status === activeTask.status && t.id !== activeId)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .forEach((t, i) => updates.push({ id: t.id, order: i }));

      // Insert into target column
      const targetTasks = tasks
        .filter((t) => t.status === targetStatus)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      let insertIdx = targetTasks.length;
      if (overTask) {
        const idx = targetTasks.findIndex((t) => t.id === overId);
        if (idx !== -1) insertIdx = idx;
      }

      targetTasks.splice(insertIdx, 0, { ...activeTask, status: targetStatus });
      targetTasks.forEach((t, i) =>
        updates.push({ id: t.id, order: i, ...(t.id === activeId ? { status: targetStatus } : {}) }),
      );
    }

    onReorder(updates);
  }

  const activeTask = activeId ? tasks.find((t) => t.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 h-full overflow-x-auto pb-4 pr-2">
        {COLUMNS.map((col) => {
          const colTasks = tasks
            .filter((t) => (t.status ?? 'not_started') === col.id)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          return (
            <KanbanColumn
              key={col.id}
              {...col}
              tasks={colTasks}
              callbacks={callbacks}
            />
          );
        })}
      </div>

      <DragOverlay>
        {activeTask ? (
          <div className="w-72 rotate-1 opacity-95 cursor-grabbing">
            <TaskCard
              task={activeTask}
              onComplete={() => {}}
              onDelete={() => {}}
              onDueDateChange={() => {}}
              onProjectChange={() => {}}
              isDragOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
